import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import apiClient from '../services/api/apiClient';

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    commission_rate: 10,
    min_payout: 5000,
    max_job_budget: 500000,
    enable_push_notifications: true,
    enable_sms_notifications: true,
    enable_email_notifications: true,
    maintenance_mode: false,
  });

  const [saved, setSaved] = useState(false);
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [operationMessage, setOperationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  // Load settings from API on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setPageLoading(true);
      setPageError(null);
      const response = await apiClient.get('/admin/settings/');
      const data = response.data.data;
      setSettings({
        commission_rate: data.commission_rate,
        min_payout: data.min_payout,
        max_job_budget: data.max_job_budget,
        enable_push_notifications: data.enable_push_notifications,
        enable_sms_notifications: data.enable_sms_notifications,
        enable_email_notifications: data.enable_email_notifications ?? true,
        maintenance_mode: data.maintenance_mode,
      });
    } catch (error: any) {
      console.error('Error loading settings:', error);
      setPageError(
        error.response?.data?.message || 'Failed to load settings from server'
      );
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
    setOperationMessage(null);
  };

  const handleSave = async () => {
    setOperationLoading('save');
    try {
      const response = await apiClient.patch('/admin/settings/', {
        commission_rate: settings.commission_rate,
        min_payout: settings.min_payout,
        max_job_budget: settings.max_job_budget,
        enable_push_notifications: settings.enable_push_notifications,
        enable_sms_notifications: settings.enable_sms_notifications,
        enable_email_notifications: settings.enable_email_notifications,
        maintenance_mode: settings.maintenance_mode,
      });

      setSaved(true);
      setOperationMessage({
        type: 'success',
        text: 'Settings saved successfully!',
      });

      if (settings.maintenance_mode) {
        setOperationMessage({
          type: 'success',
          text: '✅ Settings saved! Maintenance mode is now ENABLED.',
        });
      }

      setTimeout(() => setSaved(false), 3000);
      setTimeout(() => setOperationMessage(null), 4000);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setOperationMessage({
        type: 'error',
        text:
          error.response?.data?.message ||
          'Failed to save settings. Please try again.',
      });
      setTimeout(() => setOperationMessage(null), 4000);
    } finally {
      setOperationLoading(null);
    }
  };

  const handleCancel = () => {
    setSaved(false);
    setOperationMessage(null);
    loadSettings(); // reload from server — never reset to stale hardcoded defaults
  };

  const handleClearCache = async () => {
    setOperationLoading('clearCache');
    try {
      await apiClient.post('/admin/cache/clear/');
      setOperationMessage({
        type: 'success',
        text: '✅ Platform cache cleared successfully! Fresh data will be loaded on next requests.',
      });
      setTimeout(() => setOperationMessage(null), 4000);
    } catch (error: any) {
      setOperationMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to clear cache. Please try again.',
      });
      setTimeout(() => setOperationMessage(null), 4000);
    } finally {
      setOperationLoading(null);
    }
  };


  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Platform Settings
      </Typography>

      {pageError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {pageError}
        </Alert>
      )}

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {operationMessage && (
        <Alert severity={operationMessage.type} sx={{ mb: 3 }}>
          {operationMessage.text}
        </Alert>
      )}

      {/* Financial Settings */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Financial Settings"
          subheader="Configure payment and commission settings"
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission Rate (%)"
                type="number"
                value={settings.commission_rate}
                onChange={(e) =>
                  handleChange('commission_rate', parseFloat(e.target.value))
                }
                inputProps={{ step: 0.1, min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Payout (₦)"
                type="number"
                value={settings.min_payout}
                onChange={(e) =>
                  handleChange('min_payout', parseInt(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Job Budget (₦)"
                type="number"
                value={settings.max_job_budget}
                onChange={(e) =>
                  handleChange('max_job_budget', parseInt(e.target.value))
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Communication Settings */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Communication Settings"
          subheader="Configure notification preferences"
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enable_push_notifications}
                  onChange={(e) =>
                    handleChange('enable_push_notifications', e.target.checked)
                  }
                />
              }
              label="Enable Push Notifications"
            />
            <Typography variant="caption" color="textSecondary">
              Send push notifications to users for job updates, messages, and system alerts
            </Typography>

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.enable_sms_notifications}
                  onChange={(e) =>
                    handleChange('enable_sms_notifications', e.target.checked)
                  }
                />
              }
              label="Enable SMS Notifications"
            />
            <Typography variant="caption" color="textSecondary">
              Send SMS messages for critical updates and OTP verification
            </Typography>

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.enable_email_notifications}
                  onChange={(e) =>
                    handleChange('enable_email_notifications', e.target.checked)
                  }
                />
              }
              label="Enable Email Notifications"
            />
            <Typography variant="caption" color="textSecondary">
              Send transactional emails for job updates, payments, and platform alerts
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="System Settings"
          subheader="Configure platform behavior"
        />
        <Divider />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={settings.maintenance_mode}
                onChange={(e) =>
                  handleChange('maintenance_mode', e.target.checked)
                }
              />
            }
            label="Maintenance Mode"
          />
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            When enabled, only admins can access the platform
          </Typography>

          {settings.maintenance_mode && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Maintenance mode is currently enabled. Users will see a maintenance message when
              trying to access the platform.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Advanced Settings */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Advanced Settings"
          subheader="Server-side cache management"
        />
        <Divider />
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Cache
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
            Clears the server-side cache (maintenance mode, settings, etc.) so changes take effect immediately.
          </Typography>
          <Button
            variant="contained"
            onClick={handleClearCache}
            disabled={operationLoading === 'clearCache'}
          >
            {operationLoading === 'clearCache' ? 'Clearing...' : 'Clear Cache'}
          </Button>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={operationLoading !== null}
        >
          Save Settings
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={handleCancel}
          disabled={operationLoading !== null}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
